import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormSelect from '../../components/Forms/FormSelect';
import FormInput from '../../components/Forms/FormInput';
import Pagination from '../../components/UI/Pagination';
import { getUsers, getUserStatuses } from '../../services/users/usersService';
import { UserDto, UserStatus } from '../../services/users/types';
import { useAuth } from "react-oidc-context";

const UsersList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);

  const [users, setUsers] = useState<UserDto[]>([]);
  const [currentPage, setCurrentPage] = useState(parseInt(queryParams.get('page') || '1'));
  // const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>(queryParams.get('status') || '');
  const [userStatuses, setUserStatuses] = useState<string[]>([]);
  const [emailFilter, setEmailFilter] = useState<string>(queryParams.get('email') || '');
  const [applicationIdFilter, setApplicationIdFilter] = useState<string>(queryParams.get('application_id') || '');

  const auth = useAuth();

  const pageSize = 10;

  const fetchUsers = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view users.');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const status = selectedStatus ? selectedStatus as UserStatus : undefined;
      const email = emailFilter.trim() || undefined;
      const applicationId = applicationIdFilter.trim() || undefined;
      
      console.log('===== DEBUG PAGINATION =====');
      console.log('Current page:', currentPage);
      console.log('Page size:', pageSize);
      const response = await getUsers(
        auth.user || null,
        currentPage,
        pageSize,
        status,
        undefined, // username removed
        applicationId,
        email
      );

      // Set the users from the content field
      console.log('API Response:', response);
      console.log('Total elements:', response.total_elements);
      console.log('Current page from API:', response.current_page);
      console.log('Page size from API:', response.page_size);
      setUsers(response.content);

      // Store pagination metadata
      setTotalElements(response.total_elements);

      // Calculate total pages based on total_elements and page_size
      // const calculatedTotalPages = Math.ceil(response.total_elements / response.page_size);
      // setTotalPages(calculatedTotalPages || 1);
    } catch (err) {
      setError('Failed to fetch users. Please try again.');
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    auth.isAuthenticated, 
    auth.user, 
    currentPage, 
    pageSize, 
    selectedStatus, 
    emailFilter,
    applicationIdFilter
  ]);

  // Fetch user statuses when component mounts
  useEffect(() => {
    const loadUserStatuses = async () => {
      if (!auth.isAuthenticated) return;
      
      try {
        const statusesData = await getUserStatuses(auth.user || null);
        setUserStatuses(statusesData);
      } catch (err) {
        console.error('Error fetching user statuses:', err);
        // Fallback statuses will come from the enum
      }
    };
    
    loadUserStatuses();
  }, [auth.isAuthenticated, auth.user]);

  useEffect(() => {
    fetchUsers();
  }, [
    currentPage, 
    selectedStatus, 
    emailFilter, 
    applicationIdFilter, 
    fetchUsers
  ]);
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1); // Reset to first page when changing filter
  };
  
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') setEmailFilter(value);
    if (name === 'applicationId') setApplicationIdFilter(value);
    setCurrentPage(1); // Reset to first page when changing filter
  };
  
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers();
  };
  
  // Update URL with current filters
  const updateQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', currentPage.toString());
    if (selectedStatus) params.set('status', selectedStatus);
    if (emailFilter) params.set('email', emailFilter);
    if (applicationIdFilter) params.set('application_id', applicationIdFilter);

    // Update URL without reloading the page
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
  }, [currentPage, selectedStatus, emailFilter, applicationIdFilter, navigate, location.pathname]);

  // Call updateQueryParams whenever filters change
  useEffect(() => {
    updateQueryParams();
  }, [currentPage, selectedStatus, emailFilter, applicationIdFilter, updateQueryParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  // Function to get the appropriate badge color based on status
  const getStatusBadgeClasses = (status?: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVATED:
        return 'bg-green-100 text-green-700';
      case UserStatus.INACTIVATED:
        return 'bg-gray-100 text-gray-700';
      case UserStatus.BLOCKED:
        return 'bg-red-100 text-red-700';
      case UserStatus.RECONFIGURE:
        return 'bg-yellow-100 text-yellow-700';
      case UserStatus.ONBOARDING_IN_PROGRESS:
        return 'bg-blue-100 text-blue-700';
      case UserStatus.TICKET_OPENING:
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div>
      <PageHeader
        title="Users"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users" }
        ]}
      />
      
      <Card className="mb-6">
        <div className="p-6">
          <form onSubmit={handleSearchSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <FormInput
                label="Email"
                id="email"
                name="email"
                type="email"
                value={emailFilter}
                onChange={handleFilterChange}
                placeholder="Filter by email"
                leftIcon={<i className="fas fa-envelope text-gray-400"></i>}
              />

              <FormInput
                label="Application ID"
                id="applicationId"
                name="applicationId"
                value={applicationIdFilter}
                onChange={handleFilterChange}
                placeholder="Filter by application ID"
                leftIcon={<i className="fas fa-cube text-gray-400"></i>}
              />

              <FormSelect
                label="Status"
                id="statusFilter"
                value={selectedStatus}
                onChange={handleStatusChange}
                leftIcon={<i className="fas fa-filter text-gray-400"></i>}
                options={[
                  { value: "", label: "All Statuses" },
                  ...(userStatuses.length > 0
                    // Using API-provided statuses if available
                    ? userStatuses.map(status => ({
                        value: status,
                        label: status === "ACTIVATED" ? "ACTIVATED" :
                               status === "INACTIVATED" ? "INACTIVATED" :
                               status // Fallback for any new statuses
                      }))
                    // Fallback to enum if API fails
                    : [
                        { value: UserStatus.ACTIVATED, label: "Activated" },
                        { value: UserStatus.INACTIVATED, label: "Inactivated" },
                        { value: UserStatus.BLOCKED, label: "Blocked" },
                        { value: UserStatus.RECONFIGURE, label: "Reconfigure" },
                        { value: UserStatus.ONBOARDING_IN_PROGRESS, label: "Onboarding In Progress" },
                        { value: UserStatus.TICKET_OPENING, label: "Ticket Opening" }
                      ]
                  )
                ]}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<i className="fas fa-search mr-2"></i>}
                className="w-full sm:w-auto"
              >
                Search
              </Button>
            </div>
          </form>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={fetchUsers}
            >
              Retry
            </Button>
          </div>
        ) : users?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No users found
              {selectedStatus && ` with status: ${selectedStatus}`}.
            </p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <Link 
                          to={`/users/${user.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {user.first_name} {user.last_name || ''}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" title={user.status || 'Unknown'}>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(user.status)}`}>
                          {user.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap truncate" title={
                        user.properties?.omniChatPlatform ||
                        (user.properties?.telegramUserId ? 'TELEGRAM' :
                        (user.properties?.teamsChannelAccountRecipient ? 'TEAMS' : 'N/A'))
                      }>
                        {user.properties?.omniChatPlatform ||
                        (user.properties?.telegramUserId ? 'TELEGRAM' :
                          (user.properties?.teamsChannelAccountRecipient ? 'TEAMS' : 'N/A'))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" title={
                        user.created_at ? (
                          new Date(user.created_at).toLocaleDateString()
                        ) : 'N/A'
                      }>
                        {user.created_at ? (
                          new Date(user.created_at).toLocaleDateString()
                        ) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link to={`/users/${user.id}`}>
                            <Button variant="ghost" size="xs">
                              <i className="fas fa-eye"></i>
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {Math.ceil(totalElements / pageSize) > 1 && (
              <div className="py-3 px-6 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalElements / pageSize)}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default UsersList;